import { router, setPageContent } from "..";
import { deleteFencealarm, putFencealarm } from "../api/fencealarms_{imei}";
import { PriceWrapper, prices } from "../billing/prices";
import {
	nameField,
	ownerField,
	sharingField,
	subscriptionField,
	tagsField,
} from "../device/device_settings_fields";
import { openUnregisterDeviceModal } from "../device/remove_device_modal";
import { errorSection } from "../error-section";
import { loadingScreen } from "../loading-screen";
import { t } from "../locale";
import { removeNotificationListener } from "../mobile";
import { state } from "../state";
import { Bulma } from "../view/bulma";
import { centeredBox } from "../view/components";

/**
 * @type {import('navigo').Handler}
 */
async function fencealarmSettingsPage(ctx) {
	if (!ctx?.data?.imei) throw "missing ctx.data.imei";
	const imei = ctx.data.imei;
	const fa = state.fencealarms[imei];

	const user = await state.getActiveUser();
	const isOwner = user.Email === fa.Metadata.OwnerEmail;

	const { FencealarmMonth, FencealarmYear } = await prices;

	const voltageAlarm = voltageAlarmField(fa);
	const calibration = calibrationField(fa);
	const subscription = subscriptionField(
		fa.Subscription,
		[
			new PriceWrapper(user.Country),
			new PriceWrapper(user.Country, FencealarmMonth),
			new PriceWrapper(user.Country, FencealarmYear),
		],
		isOwner,
	);
	const sharing = sharingField(fa.Metadata);
	const tags = tagsField(fa.Metadata);
	const name = nameField(fa.Metadata.Name);
	const owner = ownerField(fa.Metadata, isOwner);

	const onClickSave = () => {
		const attempt = errorSection.attempt(async () => {
			const fa = await putFencealarm(imei, {
				VoltageAlarm: {
					Enabled: voltageAlarm.isEnabled(),
					AlarmLevel: voltageAlarm.getThreshold(),
				},
				Calibration: {
					Enabled: calibration.isEnabled(),
					ExpectedVoltage: calibration.getVoltage(),
				},
				PriceId: subscription.getPriceId(),
				Name: name.getName(),
				Access: sharing.getAccess(),
				Notify: sharing.getNotify(),
				Tags: tags.getTags(),
				Owner: owner.getOwner(),
			});
			state.fencealarms[fa.Imei] = fa;
			router.jump(`/devices?id=${fa.Imei}`);
		});
		loadingScreen.waitFor(attempt);
	};

	const unregister = () => {
		const attempt = errorSection.attempt(async () => {
			await deleteFencealarm(fa.Imei);
			delete state.fencealarms[fa.Imei];
			removeNotificationListener("fencealarm", fa.Imei);
			router.jump("/devices");
		});
		loadingScreen.waitFor(attempt);
	};

	setPageContent(
		centeredBox(
			"small",
			Bulma.title(`FenceAlarm ${fa.Metadata.Name}`, "is-4"),
			Bulma.subtitle(`IMEI: ${fa.Imei}`, "is-6"),
			voltageAlarm.element,
			calibration.element,
			subscription.element,
			sharing.element,
			tags.element,
			name.element,
			owner.element,
			Bulma.buttons(
				Bulma.button(
					{
						onClick: onClickSave,
						styles: ["is-primary"],
					},
					t.save,
				),
				Bulma.button(
					{
						onClick: () => router.jump(`/devices?id=${fa.Imei}`),
						styles: ["is-primary"],
					},
					t.back,
				),
				Bulma.button(
					{
						disabled: !isOwner,
						onClick: () => openUnregisterDeviceModal(unregister),
						styles: ["is-danger"],
					},
					t.remove,
				),
			),
		),
	);
}

/**
 * @param {Fencealarm} fa
 */
function voltageAlarmField(fa) {
	const isSubscribed =
		fa.Subscription.PriceIds.Current !== "" ||
		Date.now() < new Date(fa.Subscription.ExpiresAt).getTime();

	const onOffSelect = Bulma.select({
		disabled: !isSubscribed,
		options: [
			Bulma.option(t.on, "on", isSubscribed && fa.VoltageAlarm.Enabled),
			Bulma.option(
				t.off,
				"off",
				!isSubscribed || !fa.VoltageAlarm.Enabled,
			),
		],
	});

	/** @type {(level: number) => HTMLOptionElement} */
	const thresholdOption = (threshold) =>
		Bulma.option(
			`${threshold} V`,
			threshold.toString(),
			threshold === fa.Config.AlarmLevel,
		);

	const thresholdSelect = Bulma.select({
		options: [
			thresholdOption(2000),
			thresholdOption(3000),
			thresholdOption(4000),
			thresholdOption(5000),
			thresholdOption(6000),
			thresholdOption(7000),
			thresholdOption(8000),
		],
	});

	const element = Bulma.field(
		{},
		Bulma.label(t.fencealarm.settingsPage.voltageAlarmLabel),
		Bulma.field(
			{
				styles: ["is-grouped"],
			},
			Bulma.control({}, onOffSelect.element),
			Bulma.control({}, thresholdSelect.element),
		),
	);

	return {
		element,
		isEnabled: () => onOffSelect.getValue() === "on",
		getThreshold: () => +thresholdSelect.getValue(),
	};
}

/**
 * @param {Fencealarm} fa
 */
function calibrationField(fa) {
	const onOffSelect = Bulma.select({
		options: [
			Bulma.option(t.on, "on", fa.Calibration.ScalingFactor > 1),
			Bulma.option(t.off, "off", fa.Calibration.ScalingFactor === 1),
		],
	});

	const expectedVoltage = Bulma.input({
		type: "text",
		placeholder: t.fencealarm.settingsPage.expectedVoltageLabel,
		value:
			fa.Calibration.ScalingFactor > 1
				? fa.Calibration.ExpectedVoltage.toString()
				: "",
	});

	const field = Bulma.field(
		{},
		Bulma.label(t.fencealarm.settingsPage.calibrationLabel),
		Bulma.field(
			{
				styles: ["is-grouped"],
			},
			Bulma.control({}, onOffSelect.element),
			Bulma.control({}, expectedVoltage.element),
		),
	);

	return {
		element: field,
		isEnabled: () => onOffSelect.getValue() === "on",
		getVoltage: () => +expectedVoltage.getValue(),
	};
}

export { fencealarmSettingsPage };
